/**
 * Day-parting schedule — a faithful port of the legacy rtb_ui `day-parting.utils.ts`.
 *
 * The new contract is a per-day `{ start, end, hours, status }` where `hours` is a 24-char `0/1` mask
 * (index = hour of day, `1` = active). `start`/`end`/`status` are DERIVED from the mask (the mask is
 * authoritative); `end` is the exclusive boundary after the last active hour, rendered `11:59 PM` when
 * hour 23 is active. Historical `{ start, end }` records normalize into a mask on load. Submitted as a
 * JSON string in the single `day_parting` field. See rtb_ui/docs/features/adgroup-day-parting.md.
 */

export type DayName = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';
export interface SingleDayParting { start: string; end: string; hours: string; status: 'on' | 'off' }
export type DayPartingSchedule = Record<DayName, SingleDayParting>;

export const DAY_PARTING_DAYS: DayName[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
export const DEFAULT_START = '07:00 AM';
export const DEFAULT_END = '11:59 PM';
export const EMPTY_HOURS = '000000000000000000000000';
export const ALL_HOURS = '111111111111111111111111';
export const DEFAULT_HOURS = '000000011111111111111111'; // 07:00–23:59

const parseJson = (v: unknown): unknown => {
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v); } catch { return v; }
};
export const isHoursMask = (v: unknown): v is string => typeof v === 'string' && /^[01]{24}$/.test(v);
const clampHour = (h: number): number => Math.max(0, Math.min(24, Math.floor(h)));

export function createHoursMask(startHour: number, endHourExclusive: number): string {
  const start = clampHour(startHour);
  const end = Math.max(start, clampHour(endHourExclusive));
  let hours = '';
  for (let h = 0; h < 24; h += 1) hours += h >= start && h < end ? '1' : '0';
  return hours;
}

function parseTimeHour(value: unknown, isEnd: boolean): number {
  if (typeof value === 'number' && isFinite(value)) return clampHour(value);
  if (typeof value !== 'string') return isEnd ? 24 : 7;
  const m = value.trim().match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*([AP]M)?$/i);
  if (!m) return isEnd ? 24 : 7;
  let hour = +m[1];
  const minute = +(m[2] || 0);
  const period = m[3] ? m[3].toUpperCase() : '';
  if (period) {
    if (hour < 1 || hour > 12 || minute > 59) return isEnd ? 24 : 7;
    hour = (hour % 12) + (period === 'PM' ? 12 : 0);
  } else if (hour > 23 || minute > 59) return isEnd ? 24 : 7;
  if (isEnd && hour === 23 && minute === 59) return 24;
  return clampHour(hour);
}

function formatHour(hour: number): string {
  const n = hour % 24;
  const display = n % 12 || 12;
  const period = n < 12 ? 'AM' : 'PM';
  return `${display < 10 ? '0' : ''}${display}:00 ${period}`;
}
const formatEndHour = (endExclusive: number): string => (endExclusive >= 24 ? DEFAULT_END : formatHour(endExclusive));

export const statusFromHours = (hours: string): 'on' | 'off' => (hours.indexOf('1') === -1 ? 'off' : 'on');

/** Rebuild a day's `{ start, end, hours, status }` from its (authoritative) mask. */
export function singleFromHours(hours: string): SingleDayParting {
  const first = hours.indexOf('1');
  if (first === -1) return { start: DEFAULT_START, end: DEFAULT_END, hours, status: 'off' };
  return { start: formatHour(first), end: formatEndHour(hours.lastIndexOf('1') + 1), hours, status: statusFromHours(hours) };
}

function legacyValueToHours(value: unknown): string {
  let startHour = 7; let endHour = 24;
  if (Array.isArray(value)) { startHour = parseTimeHour(value[0], false); endHour = parseTimeHour(value[1], true); }
  else if (value && typeof value === 'object') {
    const o = value as { start?: unknown; end?: unknown };
    startHour = parseTimeHour(o.start, false); endHour = parseTimeHour(o.end, true);
  }
  return createHoursMask(startHour, endHour);
}

export function normalizeSingle(value: unknown): SingleDayParting {
  const source = parseJson(value) as { hours?: unknown; start?: unknown; end?: unknown } | unknown[] | null;
  if (source && isHoursMask((source as { hours?: unknown }).hours)) return singleFromHours((source as { hours: string }).hours);
  if (source && (Array.isArray(source) || (source as { start?: unknown }).start || (source as { end?: unknown }).end)) {
    return singleFromHours(legacyValueToHours(source));
  }
  return singleFromHours(DEFAULT_HOURS);
}

export function normalizeSchedule(value: unknown): DayPartingSchedule {
  const source = (parseJson(value) as Record<string, unknown>) || {};
  const out = {} as DayPartingSchedule;
  for (const day of DAY_PARTING_DAYS) out[day] = normalizeSingle(source[day]);
  return out;
}

/** Normalize, then JSON-stringify — the single `day_parting` payload the ad-group API expects. */
export const serializeDayParting = (value: unknown): string => JSON.stringify(normalizeSchedule(value));

export const defaultSchedule = (): DayPartingSchedule => normalizeSchedule({});
